import { sql } from "drizzle-orm";
import {
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { user } from "../auth";

export const buyingOrderStatusEnum = pgEnum("buying_order_status", ["ORDERED", "CANCELLED"]);

export const buyingOrderSourceEnum = pgEnum("buying_order_source", ["INTERNAL", "BUYLIST"]);

export const buylistOrderStatusEnum = pgEnum("buylist_order_status", [
  "submitted",
  "negotiating",
  "rejected",
  "cancelled",
  "approved",
  "invoice_submitted",
  "invoice_rejected",
  "invoice_approved",
  "shipped",
  "arrived",
  "qc_completed",
  "completed",
  "payment_sent",
]);

/**
 * One purchase header. Internal TCGPlayer / packing-list carts stay `ORDERED` /
 * `CANCELLED`. Buylist supply uses `source = BUYLIST` plus `buylist_status`.
 * Cards hang off `procurements.buying_order_id`; there is no line table.
 * Writer: platform staff (INTERNAL) or buylist ingest (BUYLIST), once that ingest
 * is mounted with a platform credential.
 */
export const buyingOrders = pgTable(
  "buying_orders",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    status: buyingOrderStatusEnum("status").notNull(),
    source: buyingOrderSourceEnum("source").notNull().default("INTERNAL"),
    externalOrderId: text("external_order_id"),
    buylistStatus: buylistOrderStatusEnum("buylist_status"),
    lastEventId: text("last_event_id"),
    lastEventAt: timestamp("last_event_at", { withTimezone: true }),
    vendor: text("vendor").notNull(),
    purchasedBy: text("purchased_by").references(() => user.id),
    orderDate: timestamp("order_date", { withTimezone: true }).notNull(),
    invoiceTotalCents: integer("invoice_total_cents").notNull(),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("buying_orders_buylist_external_idx")
      .on(t.externalOrderId)
      .where(sql`${t.source} = 'BUYLIST' and ${t.externalOrderId} is not null`),
    index("buying_orders_source_idx").on(t.source),
  ],
);

/**
 * Append-only buylist snapshot deliveries. Writer: buylist ingest, once it is
 * mounted with a platform credential. Unique on `external_event_id` so a replay
 * is a no-op.
 */
export const buyingOrderEvents = pgTable(
  "buying_order_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    buyingOrderId: uuid("buying_order_id")
      .notNull()
      .references(() => buyingOrders.id),
    externalEventId: text("external_event_id").notNull(),
    buylistStatus: buylistOrderStatusEnum("buylist_status").notNull(),
    occurredAt: timestamp("occurred_at", { withTimezone: true }).notNull(),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("buying_order_events_external_idx").on(t.externalEventId),
    index("buying_order_events_order_idx").on(t.buyingOrderId, t.createdAt),
  ],
);
